import {
  ColDef,
  ICellRendererParams,
  ValueGetterParams
} from 'ag-grid-community';
import dayjs from 'dayjs';
import { LocationPopOver } from '@grc/ui-shared';
import { IShipmentGridRow } from '../../../models/shipments/shipment-grid-row.interface';
import { ILocationEntity } from '../../../common/location-popover/location-popover.interface';
import I18N from '../../../i18n/interface';
import { getShipmentStatusBadge } from '../shipment-status';
import {
  ITransportationMode,
  IServiceChargeDetail
} from '../useShipmentSettings';

interface IGetWorkItemDetailsGridColumnsArgs {
  localeData: I18N;
  destinationsMap: Record<string, ILocationEntity>;
  carriersMap: Record<string, ILocationEntity>;
  entitiesLoading: boolean;
  transportationModes: ITransportationMode[];
  serviceCharges: IServiceChargeDetail[];
  onShipmentIdClick: (row: IShipmentGridRow) => void;
}

function formatDateTime(value?: string): string {
  if (!value) return '';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('MM/DD/YYYY hh:mm A') : '';
}

// Same rendering as shipment-grid.columns.tsx's renderLocationCell - kept in sync there rather than
// shared, since the two column sets differ enough elsewhere that a shared helper module wasn't
// worth extracting for this one function.
function renderLocationCell(
  entity: ILocationEntity | null | undefined,
  entitiesLoading: boolean,
  targetId: string
) {
  if (entitiesLoading) {
    return <span className="shipment-grid-location-loading">…</span>;
  }
  if (!entity?.name) {
    return <span className="shipment-grid-location-empty">—</span>;
  }
  return (
    <LocationPopOver
      id={`${targetId}_popover`}
      targetId={targetId}
      location={entity}
      labelNoContactToShow=""
      defaultPhoneCountry=""
      defaultContactId=""
      showDefaultContact={false}
    />
  );
}

// Columns for the MyWorkItems Details grid. ShipmentGridRow is a narrower shape than IShipment
// (see shipment-grid-row.interface.ts) - no HU/Product breakdown, and totalQuantity/
// totalWeightPounds arrive pre-aggregated instead of being summed from handlingUnits.
export function getWorkItemDetailsGridColumns({
  localeData,
  destinationsMap,
  carriersMap,
  entitiesLoading,
  transportationModes,
  serviceCharges,
  onShipmentIdClick
}: IGetWorkItemDetailsGridColumnsArgs): ColDef<IShipmentGridRow>[] {
  return [
    {
      headerName: localeData.ShipmentId,
      field: 'shipmentNumber',
      minWidth: 130,
      cellRenderer: (params: ICellRendererParams<IShipmentGridRow>) =>
        params.data ? (
          <span
            role="button"
            tabIndex={0}
            className="grc-text-link"
            onClick={() => onShipmentIdClick(params.data as IShipmentGridRow)}
            onKeyDown={e => {
              if (e.key === 'Enter' && params.data) {
                onShipmentIdClick(params.data);
              }
            }}
          >
            {params.data.shipmentNumber}
          </span>
        ) : null
    },
    {
      // TBD - ShipmentGridRow has no transport field yet (Order.Service.Shared.Models.v1.
      // ShipmentGridRow deliberately excludes it, an open backfill/denormalize decision). Left
      // blank rather than fabricated, same precedent as the old ShipmentGrid's TransportId column.
      headerName: localeData.TransportId,
      colId: 'transportId',
      valueGetter: () => '',
      minWidth: 130
    },
    {
      headerName: localeData.Status,
      field: 'status',
      valueGetter: (params: ValueGetterParams<IShipmentGridRow>) =>
        params.data?.status != null
          ? getShipmentStatusBadge(
              params.data.status,
              localeData,
              params.data.holds
            ).label
          : '',
      cellRenderer: (params: ICellRendererParams<IShipmentGridRow>) => {
        if (params.data?.status == null) return null;
        const { label, variant } = getShipmentStatusBadge(
          params.data.status,
          localeData,
          params.data.holds
        );
        return (
          <span className={`shipment-status-badge shipment-status-badge-${variant}`}>
            {label}
          </span>
        );
      },
      minWidth: 150
    },
    {
      headerName: localeData.Destination,
      field: 'destinationId',
      cellRenderer: (params: ICellRendererParams<IShipmentGridRow>) =>
        renderLocationCell(
          params.data?.destinationId
            ? destinationsMap[params.data.destinationId]
            : null,
          entitiesLoading,
          `work_item_destination_${params.data?.shipmentId}`
        ),
      minWidth: 220
    },
    {
      headerName: localeData.Expedite,
      field: 'serviceLevelId',
      valueGetter: (params: ValueGetterParams<IShipmentGridRow>) => {
        const level = serviceCharges.find(
          service => service.id === params.data?.serviceLevelId
        );
        return level?.isStandardService === false
          ? localeData.Yes
          : localeData.No;
      },
      minWidth: 110
    },
    {
      headerName: localeData.Mode,
      field: 'transportationModeId',
      valueGetter: (params: ValueGetterParams<IShipmentGridRow>) =>
        transportationModes.find(
          mode => mode.id === params.data?.transportationModeId
        )?.displayName ?? '',
      minWidth: 110
    },
    {
      headerName: localeData.PuDateAndTime,
      field: 'pickupReadyDate',
      valueGetter: (params: ValueGetterParams<IShipmentGridRow>) =>
        formatDateTime(params.data?.pickupReadyDate),
      minWidth: 170
    },
    {
      headerName: localeData.DeliveryDateAndTime,
      field: 'deliveryReadyDate',
      valueGetter: (params: ValueGetterParams<IShipmentGridRow>) =>
        formatDateTime(params.data?.deliveryReadyDate),
      minWidth: 170
    },
    {
      headerName: localeData.TotalQuantity,
      field: 'totalQuantity',
      cellStyle: { textAlign: 'right' },
      minWidth: 140
    },
    {
      headerName: localeData.TotalWeight,
      field: 'totalWeightPounds',
      valueGetter: (params: ValueGetterParams<IShipmentGridRow>) =>
        params.data?.totalWeightPounds != null
          ? `${params.data.totalWeightPounds} lb`
          : '',
      minWidth: 140
    },
    {
      headerName: localeData.Carrier,
      field: 'pickupCarrierId',
      cellRenderer: (params: ICellRendererParams<IShipmentGridRow>) =>
        renderLocationCell(
          params.data?.pickupCarrierId
            ? carriersMap[params.data.pickupCarrierId]
            : null,
          entitiesLoading,
          `work_item_carrier_${params.data?.shipmentId}`
        ),
      minWidth: 220
    }
  ];
}

export default getWorkItemDetailsGridColumns;
